/**
 * Pointer & keyboard input on the map canvas → camera moves and store actions.
 *
 *  Left click          select / inspect (counters are hit-tested first; empty hex selects the
 *                      own stack there, or clears) — with an order tool active it issues the order
 *  Left drag           rubber-band select own units (Shift = add)
 *  Right click         contextual order for the selection (attack on enemies, else move)
 *  Right drag          pan (a right press that moves > 6 px never issues an order)
 *  Middle drag         pan
 *  Wheel / pinch       zoom to cursor
 *  Arrow keys          pan (Shift = faster); edge scrolling when enabled in settings
 * Letter keys and Space are left to the HUD (A = attack tool, Space = pause …), so the
 * renderer never double-handles a key. The browser context menu is suppressed on the canvas.
 */
import { type HexId, NO_HEX } from '@friction/engine';
import type { GameStoreState } from '../game/store';
import type { Camera } from './camera';

export interface InputHost {
  canvas: HTMLCanvasElement;
  camera: Camera;
  hexAt(sx: number, sy: number): HexId;
  hitCounter(sx: number, sy: number): { unitId?: number; contactId?: number; key: string } | null;
  ownUnitsInScreenRect(x0: number, y0: number, x1: number, y1: number): number[];
  setBox(r: { x0: number; y0: number; x1: number; y1: number } | null): void;
  setHoverCounter(key: string | null): void;
  edgeScroll(): boolean;
  store(): GameStoreState;
}

type DragMode = 'pending-left' | 'box' | 'pan' | 'pending-right';

interface Drag {
  mode: DragMode;
  id: number;
  sx: number;
  sy: number;
  lx: number;
  ly: number;
}

const PAN_KEYS: Record<string, [number, number]> = {
  arrowup: [0, -1],
  arrowdown: [0, 1],
  arrowleft: [-1, 0],
  arrowright: [1, 0],
};

const isTyping = (t: EventTarget | null): boolean =>
  t instanceof HTMLElement &&
  (t.isContentEditable || t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT');

export class InputController {
  private readonly host: InputHost;
  private readonly keys = new Set<string>();
  private drag: Drag | null = null;
  private px = 0;
  private py = 0;
  private inside = false;
  private shift = false;
  private hoverVersion = -1;
  private readonly off: (() => void)[] = [];

  constructor(host: InputHost) {
    this.host = host;
    const c = host.canvas;
    const on = <K extends keyof HTMLElementEventMap>(
      el: HTMLElement | Window,
      type: K,
      fn: (e: HTMLElementEventMap[K]) => void,
      opts?: AddEventListenerOptions,
    ) => {
      el.addEventListener(type, fn as EventListener, opts);
      this.off.push(() => el.removeEventListener(type, fn as EventListener, opts));
    };
    on(c, 'pointerdown', (e) => this.down(e));
    on(c, 'pointermove', (e) => this.move(e));
    on(c, 'pointerup', (e) => this.up(e));
    on(c, 'pointercancel', () => this.cancel());
    on(c, 'pointerleave', () => {
      this.inside = false;
      if (!this.drag) this.clearHover();
    });
    on(c, 'pointerenter', (e) => {
      // Adopt the real pointer position right away: a stale (0, 0) would edge-scroll to the corner.
      [this.px, this.py] = this.local(e);
      this.inside = true;
    });
    on(c, 'wheel', (e) => this.wheel(e), { passive: false });
    on(c, 'contextmenu', (e) => e.preventDefault());
    on(c, 'auxclick', (e) => e.preventDefault());
    on(window, 'keydown', (e) => this.key(e as KeyboardEvent, true));
    on(window, 'keyup', (e) => this.key(e as KeyboardEvent, false));
    on(window, 'blur', () => {
      this.keys.clear();
      this.cancel();
    });
  }

  private local(e: PointerEvent | WheelEvent): [number, number] {
    const r = this.host.canvas.getBoundingClientRect();
    return [e.clientX - r.left, e.clientY - r.top];
  }

  private down(e: PointerEvent): void {
    if (this.drag) return;
    const [x, y] = this.local(e);
    this.shift = e.shiftKey;
    let mode: DragMode;
    if (e.button === 1) mode = 'pan';
    else if (e.button === 0) mode = 'pending-left';
    else if (e.button === 2) mode = 'pending-right';
    else return;
    e.preventDefault();
    this.host.canvas.setPointerCapture(e.pointerId);
    this.drag = { mode, id: e.pointerId, sx: x, sy: y, lx: x, ly: y };
  }

  private move(e: PointerEvent): void {
    const [x, y] = this.local(e);
    this.px = x;
    this.py = y;
    this.inside = true;
    this.shift = e.shiftKey;
    const d = this.drag;
    if (d && d.id === e.pointerId) {
      const moved = Math.hypot(x - d.sx, y - d.sy);
      if (d.mode === 'pending-left' && moved > 5) d.mode = 'box';
      if (d.mode === 'pending-right' && moved > 6) {
        d.mode = 'pan';
        this.host.camera.panBy(x - d.sx, y - d.sy);
      } else if (d.mode === 'pan') this.host.camera.panBy(x - d.lx, y - d.ly);
      if (d.mode === 'box')
        this.host.setBox({
          x0: Math.min(d.sx, x),
          y0: Math.min(d.sy, y),
          x1: Math.max(d.sx, x),
          y1: Math.max(d.sy, y),
        });
      d.lx = x;
      d.ly = y;
      if (d.mode === 'pan') return;
    }
    this.refreshHover();
  }

  private up(e: PointerEvent): void {
    const d = this.drag;
    if (!d || d.id !== e.pointerId) return;
    this.drag = null;
    if (this.host.canvas.hasPointerCapture(e.pointerId)) this.host.canvas.releasePointerCapture(e.pointerId);
    const [x, y] = this.local(e);
    const s = this.host.store();
    const shift = e.shiftKey;
    if (d.mode === 'box') {
      this.host.setBox(null);
      s.boxSelect(
        this.host.ownUnitsInScreenRect(
          Math.min(d.sx, x),
          Math.min(d.sy, y),
          Math.max(d.sx, x),
          Math.max(d.sy, y),
        ),
        shift,
      );
    } else if (d.mode === 'pending-left') {
      const hit = this.host.hitCounter(x, y);
      s.clickHex(this.host.hexAt(x, y), { shift, unitId: hit?.unitId, contactId: hit?.contactId });
    } else if (d.mode === 'pending-right') {
      const hex = this.host.hexAt(x, y);
      if (hex !== NO_HEX) s.commandHex(hex, { shift });
    }
  }

  private cancel(): void {
    if (this.drag?.mode === 'box') this.host.setBox(null);
    this.drag = null;
  }

  private wheel(e: WheelEvent): void {
    e.preventDefault();
    const [x, y] = this.local(e);
    const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? 400 : 1;
    const delta = Math.max(-300, Math.min(300, e.deltaY * unit));
    const factor = Math.exp(-delta * (e.ctrlKey ? 0.01 : 0.0022));
    this.host.camera.zoomAt(factor, x, y);
  }

  private key(e: KeyboardEvent, down: boolean): void {
    if (down && isTyping(e.target)) return;
    const k = e.key.toLowerCase();
    this.shift = e.shiftKey;
    if (!(k in PAN_KEYS)) return;
    if (down && (e.ctrlKey || e.metaKey || e.altKey)) return;
    if (down) {
      this.keys.add(k);
      e.preventDefault();
    } else this.keys.delete(k);
  }

  private clearHover(): void {
    const s = this.host.store();
    if (s.hoverHex !== NO_HEX) s.setHover(NO_HEX);
    this.host.setHoverCounter(null);
  }

  private refreshHover(): void {
    if (!this.inside) return;
    const hex = this.host.hexAt(this.px, this.py);
    this.host.store().setHover(hex);
    this.host.setHoverCounter(this.host.hitCounter(this.px, this.py)?.key ?? null);
    this.hoverVersion = this.host.camera.version;
  }

  /** Per frame: keyboard / edge scrolling; keep the hover hex under a still pointer. */
  update(dt: number): void {
    let vx = 0;
    let vy = 0;
    for (const k of this.keys) {
      const v = PAN_KEYS[k];
      if (v) {
        vx += v[0];
        vy += v[1];
      }
    }
    if (this.inside && !this.drag && this.host.edgeScroll() && document.hasFocus()) {
      const m = 14;
      const c = this.host.camera;
      if (this.px < m) vx -= 1;
      else if (this.px > c.screenW - m) vx += 1;
      if (this.py < m) vy -= 1;
      else if (this.py > c.screenH - m) vy += 1;
    }
    if (vx || vy) {
      const speed = (this.shift ? 1700 : 850) * dt;
      const l = Math.hypot(vx, vy);
      this.host.camera.panBy((-vx / l) * speed, (-vy / l) * speed);
    }
    if (this.inside && this.host.camera.version !== this.hoverVersion && this.drag?.mode !== 'pan')
      this.refreshHover();
  }

  destroy(): void {
    for (const f of this.off) f();
    this.off.length = 0;
    this.keys.clear();
  }
}
